'use client';

/**
 * Enhanced Theme Provider
 * Integrates all framework capabilities
 */
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { ThemeLoader as ThemeLoadingIndicator } from '@/components/common/SuspenseLoader';
import { logger } from '@/lib/logger';
import { applyTheme } from '@/lib/theme/theme';

import { useConfiguration } from '../core/config/hooks';
import { createThemeLoader } from '../core/theme/loader';
import { getThemeRegistry } from '../core/theme/registry';
import { ThemeDesignSystem, ThemeManifest, ThemeMode } from '../core/theme/types';

// Note: Theme routes are registered server-side in @/lib/theme-registry
// Do NOT import theme routes here as it would bundle server-only code into the client

interface EnhancedThemeContextType {
  manifest: ThemeManifest | null;
  design: ThemeDesignSystem | null;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  isLoading: boolean;
  error: Error | null;
}

const EnhancedThemeContext = createContext<EnhancedThemeContextType | undefined>(undefined);

export const useEnhancedTheme = () => {
  const context = useContext(EnhancedThemeContext);
  if (!context) {
    throw new Error('useEnhancedTheme must be used within EnhancedThemeProvider');
  }
  return context;
};

export interface EnhancedThemeProviderProps {
  children: ReactNode;
  initialThemeId?: string;
  initialMode?: ThemeMode;
}

export const EnhancedThemeProvider: React.FC<EnhancedThemeProviderProps> = ({ children, initialThemeId, initialMode }) => {
  const { config, loading: configLoading } = useConfiguration();

  // Use config values with fallbacks
  const themeId = config?.theme?.activeTheme || initialThemeId || 'cray';
  const mode = config?.theme?.mode || initialMode || 'light';

  const [manifest, setManifest] = useState<ThemeManifest | null>(null);
  const [design, setDesign] = useState<ThemeDesignSystem | null>(null);
  const [currentMode, setModeState] = useState<ThemeMode>(mode as ThemeMode);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Wait for config to be loaded
    if (configLoading || !config) {
      return;
    }

    const loadTheme = async () => {
      try {
        setIsLoading(true);
        setError(null);
        logger.debug({ msg: 'Loading theme', themeId, mode: currentMode });

        const loader = createThemeLoader();
        const registry = getThemeRegistry();

        // // Load theme routes for all available themes
        // const availableThemes = config?.theme?.availableThemes || ['cray'];
        // await loadThemeRoutes(availableThemes);

        // Load manifest
        logger.debug({ msg: 'Loading theme manifest', themeId });
        const loadedManifest = await loader.loadManifest(themeId);
        registry.registerTheme(loadedManifest);
        registry.setActiveTheme(themeId);
        setManifest(loadedManifest);
        logger.debug({ msg: 'Theme manifest loaded', themeId, version: loadedManifest.version });

        // Load design system
        logger.debug({ msg: 'Loading theme design system', themeId, mode: currentMode });
        const loadedDesign = await loader.loadDesign(themeId, currentMode);
        setDesign(loadedDesign);
        logger.debug({ msg: 'Theme design system loaded', themeId, mode: currentMode });

        // Apply CSS variables
        // applyThemeToCSS(loadedDesign); //legacy
        applyTheme(loadedDesign);
        logger.debug({ msg: 'Theme CSS variables applied' });

        setIsLoading(false);
      } catch (err) {
        logger.error({ msg: 'Failed to load theme', error: err, themeId, mode: currentMode });
        setError(err as Error);
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [configLoading, config, themeId, currentMode]);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('theme-mode', newMode);
    }
  };

  const toggleMode = () => {
    setMode(currentMode === 'light' ? 'dark' : 'light');
  };

  const value: EnhancedThemeContextType = {
    manifest,
    design,
    mode: currentMode,
    setMode,
    toggleMode,
    isLoading,
    error,
  };

  if (isLoading) {
    return <ThemeLoadingIndicator />;
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1>Theme Loading Error</h1>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }

  return <EnhancedThemeContext.Provider value={value}>{children}</EnhancedThemeContext.Provider>;
};

/**
 * Apply theme design system to CSS variables
 */
function applyThemeToCSS(design: ThemeDesignSystem) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  // Apply colors
  Object.entries(design.colors).forEach(([key, value]) => {
    if (value) root.style.setProperty(`--color-${key}`, value);
  });

  // Apply typography
  if (design.typography) {
    Object.entries(design.typography.fontFamily || {}).forEach(([key, value]) => {
      if (value) root.style.setProperty(`--font-family-${key}`, value);
    });

    Object.entries(design.typography.fontSize || {}).forEach(([key, value]) => {
      root.style.setProperty(`--font-size-${key}`, value);
    });

    Object.entries(design.typography.fontWeight || {}).forEach(([key, value]) => {
      root.style.setProperty(`--font-weight-${key}`, String(value));
    });

    if (design.typography.lineHeight) {
      Object.entries(design.typography.lineHeight).forEach(([key, value]) => {
        root.style.setProperty(`--line-height-${key}`, String(value));
      });
    }
  }

  // Apply spacing
  if (design.spacing) {
    Object.entries(design.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-theme-${key}`, value);
    });
  }

  // Apply border radius
  if (design.borderRadius) {
    Object.entries(design.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-theme-${key}`, value);
    });
  }

  // Apply shadows
  if (design.shadows) {
    Object.entries(design.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });
  }

  // Apply transitions
  if (design.transitions) {
    Object.entries(design.transitions).forEach(([key, value]) => {
      root.style.setProperty(`--transition-theme-${key}`, value);
    });
  }

  // Apply z-index
  if (design.zIndex) {
    Object.entries(design.zIndex).forEach(([key, value]) => {
      root.style.setProperty(`--z-index-theme-${key}`, String(value));
    });
  }
}
